import React from "react";
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import './Toggle.css';

const Toggle = (props) => {
    const { items, changeHandler, value, style = "segmented" } = props;

    // Helper to get value and label from item (supports both string and {label, value} objects)
    const getItemValue = (item) => typeof item === 'object' ? item.value : item;
    const getItemLabel = (item) => {
        if (typeof item === 'object') return item.label;
        // Special case: "cpu" should be displayed as "CPU"
        if (item.toLowerCase() === 'cpu') return 'CPU';
        return item.charAt(0).toUpperCase() + item.slice(1);
    };

    // Buttons style: bordered options, selected = lightblue border + lightblue10 bg (matches period type / budget definition)
    if (style === "buttons") {
        return (
            <div className="flex gap-3 cursor-pointer">
                {items.map((item, index) => {
                    const itemValue = getItemValue(item);
                    const itemLabel = getItemLabel(item);
                    const isSelected = value === itemValue;
                    return (
                        <button
                            key={index}
                            type="button"
                            onClick={() => changeHandler(itemValue)}
                            className={`flex items-center justify-center gap-2 px-3 py-2 rounded border text-sm font-medium transition-colors duration-100 ${
                                isSelected
                                    ? "border-primary border-solid bg-primary-soft text-primary hover:bg-primary-soft cursor-pointer"
                                    : "border-border border-solid bg-transparent text-text-2 hover:bg-surface-2 cursor-pointer"
                            }`}
                        >
                            {itemLabel}
                        </button>
                    );
                })}
            </div>
        );
    }

    // Pills style: used in HierarchyManager
    if (style === "pills") {
        return (
            <div className="flex gap-1 p-2 bg-side rounded-lg border border-border w-fit">
                {items.map((item, index) => {
                    const itemValue = getItemValue(item);
                    const itemLabel = getItemLabel(item);
                    return (
                        <>
                        <button
                            key={index}
                            type="button"
                            onClick={() => changeHandler(itemValue)}
                            className={`px-3 py-1.5 rounded text-base font-medium transition-colors ${
                                value === itemValue
                                    ? "bg-primary rounded-lg text-white hover:bg-primary-hover"
                                    : "text-text-2 rounded-lg hover:bg-surface-2 bg-transparent"
                            }`}
                        >
                            {itemLabel}
                        </button>
                        </>
                    );
                })}
            </div>
        );
    }

    // Constraint style: mimics ActiveConstraintsBar chips
    if (style === "constraint") {
        return (
            <div className="flex gap-2">
                {items.map((item, index) => {
                    const itemValue = getItemValue(item);
                    const itemLabel = getItemLabel(item);
                    return (
                        <button
                            key={index}
                            type="button"
                            onClick={() => changeHandler(itemValue)}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                                value === itemValue
                                    ? "bg-primary text-white hover:bg-primary-hover"
                                    : "bg-surface-2 text-text-2 hover:bg-surface-3"
                            }`}
                        >
                            {itemLabel}
                        </button>
                    );
                })}
            </div>
        );
    }

    // Active/Draft style: used in HierarchyManager for Active/Draft toggle
    if (style === "active-draft") {
        const { disabledItems = [] } = props; // Array of values that should be disabled
        return (

            <>
                {props.label && (
                    <span className="text-text-2 text-sm mr-2 font-medium cursor-default">{props.label}</span>
                )}

                <div className="flex rounded-md border border-border bg-side overflow-hidden">
                    {items.map((item, index) => {
                        const itemValue = getItemValue(item);
                        const itemLabel = getItemLabel(item);
                        const isSelected = value === itemValue;
                        const isDisabled = disabledItems.includes(itemValue);
                        const hasIndicator = item.indicator; // Support for dot indicator
                        
                        return (
                            <button
                                key={index}
                                type="button"
                                onClick={() => !isDisabled && changeHandler(itemValue)}
                                disabled={isDisabled}
                                className={`px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                                    isSelected
                                        ? "bg-primary text-white"
                                        : isDisabled
                                            ? "text-text-3 cursor-not-allowed bg-surface-2"
                                            : "text-text-2 hover:bg-surface-2 cursor-pointer"
                                }`}
                            >
                                {itemLabel}
                                {hasIndicator && (
                                    <span
                                        className="w-1.5 h-1.5 rounded-full bg-current opacity-90 shrink-0"
                                        aria-hidden
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            </>
        );
    }

    // Segmented style: default Radix UI implementation
    return (
        <ToggleGroup.Root 
            className="ToggleGroup my-auto cursor-pointer border-b border-border"
            type="single" 
            // defaultValue={defaultValue} 
            value={value}
            aria-label="Text alignment"
            onValueChange={(value) => {
                if (value) changeHandler(value);
            }}
        >
            {items.map((item, index) => {
                const itemValue = getItemValue(item);
                const itemLabel = getItemLabel(item);
                return (
                    <ToggleGroup.Item key={index} className="ToggleGroupItem" value={itemValue} aria-label={itemLabel}>
                        {itemLabel}
                    </ToggleGroup.Item>
                );
            })}
        </ToggleGroup.Root>
    )
}

export default Toggle;